'use client'

import { useEffect, useState } from 'react'
import { isChunkLoadError, recoverFromChunkError } from '@/lib/chunkError'

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  // Track connectivity so an error caused by being offline shows the right
  // message and recovers on its own. This boundary renders its own document, so
  // the global OfflineIndicator isn't mounted here; we handle offline inline.
  const [isOffline, setIsOffline] = useState(false)
  const chunkError = isChunkLoadError(error)

  useEffect(() => {
    console.error('[GlobalError]', error)
  }, [error])

  useEffect(() => {
    setIsOffline(!navigator.onLine)
    const handleOnline = () => {
      setIsOffline(false)
      // Connectivity is back. Chunk errors need a reload (webpack cached the
      // failed promise); other errors can just retry the render.
      if (chunkError) {
        recoverFromChunkError()
      } else {
        reset()
      }
    }
    const handleOffline = () => setIsOffline(true)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    // Already online with a chunk error: reload immediately to re-fetch it.
    if (chunkError && navigator.onLine) {
      recoverFromChunkError()
    }
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [chunkError, reset])

  const handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/dashboard'
    }
  }

  const isDev = process.env.NODE_ENV !== 'production'

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
          background: '#0b0b0c',
          color: '#f5f5f5',
          padding: '1.5rem',
        }}
      >
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>
            {isOffline ? 'No internet connection' : 'Something went wrong'}
          </h1>
          <p style={{ marginTop: '0.75rem', color: '#a0a0a0', fontSize: '0.9rem' }}>
            {isOffline
              ? 'You appear to be offline. We’ll reconnect automatically once your connection is back.'
              : 'We hit an unexpected error. Try refreshing the page, or head back to the dashboard.'}
          </p>
          {isDev && error?.message && (
            <pre
              style={{
                marginTop: '1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                background: '#161618',
                border: '1px solid #2a2a2d',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                overflow: 'auto',
                maxHeight: '12rem',
              }}
            >
              {error.message}
            </pre>
          )}
          <div
            style={{
              marginTop: '1.5rem',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
              justifyContent: 'center',
            }}
          >
            <button
              onClick={chunkError ? () => window.location.reload() : reset}
              style={{
                background: '#c4f000',
                color: '#0b0b0c',
                fontWeight: 600,
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              onClick={handleGoHome}
              style={{
                background: 'transparent',
                color: '#f5f5f5',
                border: '1px solid #2a2a2d',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                cursor: 'pointer',
              }}
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </body>
    </html>
  )
}
